import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { BlameDeactivationReasons, DEACTIVATION_REASON_LIST } from '@vendasta/accounts/legacy';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AppEditionService, EditionIdentifier } from '../../activation/core/app-edition.service';

export interface DeactivateDialogDataInterface {
  itemName: string;
  itemId: string;
  currentlyTrial: boolean;
  currentEditionId: string;
  subItemAmount?: number;
  displayDate: string;
  useCommitment: boolean;
  billingFrequency: string;
  shouldDisplayDate: boolean;
  dependentProducts: DeactivateDialogDataInterface[];
}

export interface DeactivateDialogResult {
  reasons: BlameDeactivationReasons;
  isDowngrade: boolean;
  editionToDowngradeTo: string;
}

const ownedAndOperatedProEditions: Map<string, string> = new Map<string, string>([
  ['RM', ''], // Reputation Management | Pro
  ['SM', ''], // Social Marketing | Pro
  ['MP-fba21121b71148c9bb33e11fcd92d520', ''], // Customer Voice | Pro (demo)
  ['MP-c4974d390a044c28aec31e421aa662b2', ''], // Customer Voice | Pro (prod)
]);
const ownedAndOperatedExpressEditions: Map<string, Map<Environment, string>> = new Map<
  string,
  Map<Environment, string>
>([
  [
    'RM',
    new Map<Environment, string>([
      [Environment.DEMO, 'EDITION-38SMW45H'],
      [Environment.PROD, 'EDITION-F7JZ5TV8'],
    ]),
  ],
  [
    'SM',
    new Map<Environment, string>([
      [Environment.DEMO, 'EDITION-SWVF3WH8'],
      [Environment.PROD, 'EDITION-FVGBNLVZ'],
    ]),
  ],
  ['MP-fba21121b71148c9bb33e11fcd92d520', new Map<Environment, string>([[Environment.DEMO, 'EDITION-4WWZC3RJ']])],
  ['MP-c4974d390a044c28aec31e421aa662b2', new Map<Environment, string>([[Environment.PROD, 'EDITION-TC8HJZNS']])],
]);

@Component({
  styleUrls: ['deactivate-dialog.component.scss'],
  templateUrl: 'deactivate-dialog.component.html',
  standalone: false,
})
export class DeactivateDialogComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<DeactivateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeactivateDialogDataInterface,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private appEditionService: AppEditionService,
    private environmentService: EnvironmentService,
    private snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
  ) {
    this.title = `Cancel ${data.itemName}`;
    this.deactivationReasonMsg = `Please give AT LEAST ONE reason why you're canceling ${data.itemName}. Your answer(s) will help us make better products.`;
  }
  reasons = DEACTIVATION_REASON_LIST;

  additionalInformationFormControl: UntypedFormControl;
  deactivationReasonFormArray: UntypedFormArray;
  deactivationFormGroup: UntypedFormGroup;
  deactivationInfo: EventEmitter<BlameDeactivationReasons> = new EventEmitter();

  pageLoaded = false;
  promoteDowngrade: boolean;
  title: string;
  downgradeSavings: string;
  deactivationReasonMsg: string;
  warnings: string[] = [];

  expressEdition: string;

  static createDeactivateReasonFormArray(deactivationReasons: string[]): UntypedFormArray {
    const deactivationReasonFormArray = deactivationReasons.map(() => new UntypedFormControl(false, []));
    return new UntypedFormArray(deactivationReasonFormArray);
  }

  static getChoiceValues(deactivationReasonsFormArray: UntypedFormArray, deactivationReasons: string[]): string[] {
    return deactivationReasonsFormArray.controls
      .map((controls: UntypedFormControl, index) => (controls && controls.value ? deactivationReasons[index] : null))
      .filter((value) => value);
  }

  ngOnInit(): void {
    const dependentProductsWarningMessages = (this.data.dependentProducts || []).map((item) => {
      return this.createWarningText(item);
    });
    this.warnings.push(this.createWarningText(this.data), ...dependentProductsWarningMessages);

    this.additionalInformationFormControl = new UntypedFormControl('', []);
    this.deactivationReasonFormArray = DeactivateDialogComponent.createDeactivateReasonFormArray(this.reasons);
    this.deactivationFormGroup = new UntypedFormGroup({
      additionalInformationFormControl: this.additionalInformationFormControl,
      deactivationReasonFormArray: this.deactivationReasonFormArray,
    });

    this.promoteDowngrade =
      !this.data.currentlyTrial &&
      ownedAndOperatedProEditions.has(this.data.itemId) &&
      ownedAndOperatedProEditions.get(this.data.itemId) === this.data.currentEditionId;

    if (this.promoteDowngrade) {
      const proEdition = ownedAndOperatedProEditions.get(this.data.itemId);
      this.expressEdition = ownedAndOperatedExpressEditions
        .get(this.data.itemId)
        .get(this.environmentService.getEnvironment());

      const editionIdentifiers: EditionIdentifier[] = [
        { appId: this.data.itemId, editionId: proEdition },
        { appId: this.data.itemId, editionId: this.expressEdition },
      ];

      combineLatest([this.appEditionService.getEditionsForEditionIdentifiers(editionIdentifiers), this.partnerId$])
        .pipe(
          switchMap(([editionsMap, partnerId]) => {
            const appEditions = editionsMap.get(this.data.itemId);
            return this.appEditionService.getMultiPurchaseCost(partnerId, [
              appEditions.get(proEdition),
              appEditions.get(this.expressEdition),
            ]);
          }),
          take(1),
        )
        .subscribe(
          (purchaseCosts) => {
            const proPrice = purchaseCosts[0];
            const expressPrice = purchaseCosts[1];

            const dollarSavings = proPrice.cost.totalCost - expressPrice.cost.totalCost;
            const percentSavings = (dollarSavings / (proPrice.cost.totalCost || 1)) * 100;
            this.downgradeSavings = `If your client may still find value in this product, consider downgrading to Express
          to maintain limited functionality and reduce costs by $${(dollarSavings / 100).toFixed(
            2,
          )}/${percentSavings.toFixed(0)}%.
          Products can always be upgraded at a later date.`;
          },
          () => {
            this.downgradeSavings = `If your client may still find value in this product, consider downgrading to Express
          to maintain limited functionality and reduce costs. Products can always be upgraded at a later date.`;
          },
          () => (this.pageLoaded = true),
        );
    } else {
      this.pageLoaded = true;
    }
  }

  downgradeProduct(): void {
    this.close(true);
  }

  confirmDelete(): void {
    this.close(false);
  }

  close(isDowngrade: boolean): void {
    const deactivationInfo = new BlameDeactivationReasons();
    deactivationInfo.choices = DeactivateDialogComponent.getChoiceValues(
      this.deactivationReasonFormArray,
      this.reasons,
    );

    if (deactivationInfo.choices.length === 0) {
      this.snackbarService.errorSnack('A reason must be selected when cancelling a product or service');
      return;
    }

    deactivationInfo.comments = this.additionalInformationFormControl.value;
    if (deactivationInfo.choices.includes('Other') && deactivationInfo.comments === '') {
      this.additionalInformationFormControl.setErrors({ cannotBeEmpty: false });
      this.snackbarService.errorSnack('When selecting Other, please provide additional information');
      return;
    }

    if (!isDowngrade) {
      this.deactivationInfo.emit(deactivationInfo);
    }

    this.dialogRef.close({
      reasons: deactivationInfo,
      isDowngrade: isDowngrade,
      editionToDowngradeTo: this.expressEdition,
    });
  }

  createWarningText(data: DeactivateDialogDataInterface): string {
    let warning: string;

    if (!data.shouldDisplayDate) {
      if (data.useCommitment) {
        warning = this.translateService.instant(
          'MANAGE_ACCOUNTS.DEACTIVATION_WARNING_MESSAGES.DEACTIVATION_WITH_COMMITMENT',
          {
            billingFrequency: data.billingFrequency,
            productName: data.itemName,
            displayDate: data.displayDate,
          },
        );
      } else {
        warning = this.translateService.instant(
          'MANAGE_ACCOUNTS.DEACTIVATION_WARNING_MESSAGES.DEACTIVATION_NO_COMMITMENT',
          {
            productName: data.itemName,
            displayDate: data.displayDate,
          },
        );
      }
    } else {
      warning = this.translateService.instant('MANAGE_ACCOUNTS.DEACTIVATION_WARNING_MESSAGES.DEACTIVATION_INSTANT', {
        productName: data.itemName,
      });
    }
    if (data.subItemAmount && data.subItemAmount > 0) {
      if (!data.shouldDisplayDate) {
        //Append
        warning +=
          ' ' +
          this.translateService.instant('MANAGE_ACCOUNTS.DEACTIVATION_WARNING_MESSAGES.DEACTIVATION_WITH_ADDON', {
            subItemAmount: data.subItemAmount,
            productName: data.itemName,
          });
      } else {
        //Overwrite
        warning = this.translateService.instant(
          'MANAGE_ACCOUNTS.DEACTIVATION_WARNING_MESSAGES.DEACTIVATION_WITH_ADDON_WAITING_FOR_ADDON',
          {
            productName: data.itemName,
            subItemAmount: data.subItemAmount,
          },
        );
      }
    }

    return warning;
  }
}
